export type { AudioSettings, StrikeAudioEvent, TransmutationAudioEvent, ResonanceAudioEvent } from './audio';

// ── Enums & Constants ──

export const FREQUENCY_COUNT = 12;

export enum PathwayType {
  Clear = 'clear',
  Amplifying = 'amplifying',
  Dampening = 'dampening',
  Inverting = 'inverting',
  Filtering = 'filtering',
  Resonant = 'resonant',
}

export enum HarmonicRequirement {
  Unison = 0,
  MinorThird = 3,
  MajorThird = 4,
  PerfectFourth = 5,
  PerfectFifth = 7,
  // Octave is identical to Unison for mod-12; will be distinguished by amplitude in later tiers
}

/** All valid harmonic intervals as mod-12 differences. */
export const HARMONIC_INTERVALS: Record<string, number> = {
  unison: 0,
  m3: 3,
  M3: 4,
  P4: 5,
  P5: 7,
} as const;

// ── Core Data ──

export interface Node {
  id: string;
  x: number; // 0.0–1.0 normalized
  y: number; // 0.0–1.0 normalized
  frequency: number; // 1–12
  locked: boolean;
  resonator?: ResonatorConfig;
}

export interface ResonatorConfig {
  triggerFrequency: number; // fires when node reaches this frequency
}

export interface Pathway {
  id: string;
  source: string; // node id
  target: string; // node id
  type: PathwayType;
  harmonic: string; // key into HARMONIC_INTERVALS, e.g. 'P5', 'm3'
  directed: boolean;
  filterThreshold?: number; // for Filtering pathways
  dynamicSuccessor?: PathwayType; // for Dynamic pathways — type changes to this after energy traverses
}

export interface BoardState {
  nodes: Node[];
  pathways: Pathway[];
}

// ── Puzzle Definition ──

export interface PuzzleMetadata {
  title: string;
  category: 'harmonic' | 'flow' | 'cascade' | 'constraint' | 'composition';
  author: string;
  created: string;
}

export interface PuzzleConstraints {
  strikeBudget: number;
  minimumStrikes: number;
  maxPropagationRings: number;
}

export interface PuzzleSolution {
  strikes: string[]; // node ids in order
  optimal: boolean;
}

export interface PuzzleDefinition {
  id: string;
  version: number;
  tier: number;
  metadata: PuzzleMetadata;
  board: BoardState;
  constraints: PuzzleConstraints;
  solutions: PuzzleSolution[];
}

// ── Engine Results ──

export interface Transmutation {
  nodeId: string;
  oldFrequency: number;
  newFrequency: number;
  ring: number;
  pathwayId: string; // pathway through which energy arrived
}

export interface ChainStep {
  resonatorNodeId: string;
  transmutations: Transmutation[];
}

export interface PathwayChange {
  pathwayId: string;
  oldType: PathwayType;
  newType: PathwayType;
}

export interface StrikeResult {
  newBoard: BoardState;
  transmutations: Transmutation[];
  struckNodeId: string;
  chainSteps: ChainStep[];        // chain reaction steps (empty if no resonators triggered)
  pathwayChanges: PathwayChange[]; // dynamic pathway type changes
}

export interface PathwayStatus {
  pathwayId: string;
  satisfied: boolean;
  currentDifference: number;
  requiredDifference: number;
  nearConsonant: boolean; // within 1 semitone of target
}

export interface HarmonyReport {
  pathways: PathwayStatus[];
  resonancePercentage: number;
  totalPathways: number;
  satisfiedCount: number;
}

// ── Scoring ──

export interface PuzzleResult {
  tier: number;
  strikesUsed: number;
  strikeBudget: number;
  undoUsed: boolean;
  solveTimeSeconds: number;
  targetTimeSeconds: number;
  resetsUsed: number;
}

export interface Score {
  base: number;
  strikeBonus: number;
  undoBonus: number;
  timeBonus: number;
  resetPenalty: number;
  total: number;
}

// ── Theme ──

export interface FrequencyColorMap {
  [frequency: number]: string;
}

export const FREQUENCY_COLORS: FrequencyColorMap = {
  1: '#E63946',  // C  — Deep Red
  2: '#F77F00',  // C# — Burnt Orange
  3: '#FCBF49',  // D  — Amber Gold
  4: '#A7C957',  // D# — Chartreuse
  5: '#2D6A4F',  // E  — Emerald
  6: '#1B9AAA',  // F  — Teal
  7: '#277DA1',  // F# — Azure
  8: '#3A0CA3',  // G  — Indigo
  9: '#7209B7',  // G# — Violet
  10: '#C77DFF', // A  — Magenta
  11: '#F72585', // A# — Rose
  12: '#E8E8E4', // B  — Silver White
};

export const FREQUENCY_GLOW_COLORS: FrequencyColorMap = {
  1: '#FF6B6B',
  2: '#FFA94D',
  3: '#FFE066',
  4: '#C5E663',
  5: '#51CF66',
  6: '#3BC9DB',
  7: '#4DABF7',
  8: '#7950F2',
  9: '#B197FC',
  10: '#E599F7',
  11: '#F783AC',
  12: '#F8F9FA',
};

export const FREQUENCY_NAMES: Record<number, string> = {
  1: 'C', 2: 'C#', 3: 'D', 4: 'D#', 5: 'E', 6: 'F',
  7: 'F#', 8: 'G', 9: 'G#', 10: 'A', 11: 'A#', 12: 'B',
};
